import { IconButton, TextButton } from '@creditornot/cb-button';
import { Column, DataTable } from '@creditornot/cb-data-table';
import { CaretDown, CaretLeft, CaretRight } from '@creditornot/cb-icons';
import { cbSemanticCssVars } from '@creditornot/cb-ingredients';
import { MenuActionItem, MenuItemGroup } from '@creditornot/cb-menu';
import { LegacyPopover } from '@creditornot/cb-popover';
import React, { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import { Category, ProductHierarchyLevel } from 'apis/restaurant-api/merchant-admin/menu2/types';
import { RestockingRule, RestockingRuleLinks, RestockingRuleType } from 'apis/restocking-rules/types';
import { Typography as T } from 'common/typography';
import { isDefined, localize } from 'common/utils';
import { flattenCategories } from 'modules/menu/utils';

import RestockingRuleFrequency from './RestockingRuleFrequency';

type Props = {
  data: { items: RestockingRule[]; links: RestockingRuleLinks } | undefined;
  categories: Category[];
  productFamilies: ProductHierarchyLevel[];
  language: Language;
  onEditClick: (rule: RestockingRule) => void;
  onDeleteClick: (ruleId: string) => void;
  onNextPageClick: (cursor: string) => void;
  onPrevPageClick: (cursor: string) => void;
  onPageSizeChange: (size: number) => void;
  pageSize: number;
  loading: boolean;
};

const Pagination = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 24px;
  border-block-start: 1px solid ${cbSemanticCssVars.borderPrimary};
`;

const PageButton = styled(IconButton).attrs({
  background: 'transparent',
  size: 'tiny',
  variant: 'neutral',
})`
  & + & {
    margin-inline-start: 12px;
  }
`;

const PageSizeOptions = styled(MenuItemGroup)`
  padding: 0 8px;
`;

const PageSizeOption = styled(MenuActionItem).attrs({
  variant: 'blue',
})`
  min-width: 60px;
`;

const DeleteButton = styled(TextButton)`
  margin-inline-start: 12px;
`;

const PAGE_SIZES = [5, 10, 20];

const REMINDER_TYPE_TRANSLATIONS_MAP: Record<RestockingRuleType, string> = {
  AUTO_RESTOCKING: 'menu.restockingRules.type.autoRestock',
  REMINDER: 'menu.restockingRules.type.reminder',
};

const RestockingRulesTable = ({
  data,
  categories,
  language,
  productFamilies,
  onEditClick,
  onNextPageClick,
  onPrevPageClick,
  onPageSizeChange,
  onDeleteClick,
  pageSize,
  loading,
}: Props) => {
  const intl = useIntl();
  const categoriesAndSubcategories = flattenCategories(categories);
  const columns: Column<RestockingRule, Record<string, unknown>>[] = useMemo(
    () => [
      {
        id: 'name',
        style: {
          width: '15%',
          textTransform: 'uppercase',
        },
        name: intl.formatMessage({ id: 'menu.restockingRules.columns.name' }),
        render: ({ record }) => <T.Body3>{record.name}</T.Body3>,
      },
      {
        id: 'categories',
        style: {
          width: '20%',
          textTransform: 'uppercase',
        },
        name: intl.formatMessage({ id: 'menu.restockingRules.columns.categories' }),
        render: ({ record }) => (
          <T.Body3>
            {record.filters
              .map(filter => {
                if (filter.type !== 'CATEGORY' && filter.type !== 'SUB_CATEGORY') {
                  return undefined;
                }
                const cat = categoriesAndSubcategories.find(cat => cat.id === filter.identifier);
                return !cat ? undefined : localize(cat.name, language);
              })
              .filter(isDefined)
              .join(', ')}
          </T.Body3>
        ),
      },
      {
        id: 'product-families',
        style: {
          width: '20%',
          textTransform: 'uppercase',
        },
        name: intl.formatMessage({ id: 'menu.restockingRules.columns.productFamilies' }),
        render: ({ record }) => (
          <T.Body3>
            {record.filters
              .map(filter => {
                if (filter.type !== 'PRODUCT_HIERARCHY') {
                  return undefined;
                }
                const productFamily = productFamilies.find(productFamily => productFamily.id === filter.identifier);
                return !productFamily ? undefined : localize(productFamily.name, language);
              })
              .filter(isDefined)
              .join(', ')}
          </T.Body3>
        ),
      },
      {
        id: 'type',
        style: {
          width: '10%',
          textTransform: 'uppercase',
        },
        name: intl.formatMessage({ id: 'menu.restockingRules.columns.type' }),
        render: ({ record }) => (
          <T.Body3>{intl.formatMessage({ id: REMINDER_TYPE_TRANSLATIONS_MAP[record.type] })}</T.Body3>
        ),
      },
      {
        id: 'frequency',
        style: {
          width: '10%',
          textTransform: 'uppercase',
        },
        name: intl.formatMessage({ id: 'menu.restockingRules.columns.frequency' }),
        render: ({ record }) => <RestockingRuleFrequency data={record.schedule} />,
      },
      {
        id: 'actions',
        name: '',
        renderName: false,
        alignContent: 'right',
        style: {
          width: '15%',
        },
        render: ({ record }) => {
          return (
            <div>
              <TextButton onClick={() => onEditClick(record)} size="small" variant="brand">
                {intl.formatMessage({ id: 'menu.restockingRules.editButton' })}
              </TextButton>
              <DeleteButton onClick={() => onDeleteClick(record.id)} size="small" variant="negative">
                {intl.formatMessage({ id: 'menu.restockingRules.deleteButton' })}
              </DeleteButton>
            </div>
          );
        },
      },
    ],
    [intl, categoriesAndSubcategories, language, productFamilies, onEditClick, onDeleteClick],
  );

  const handlePreviousPageClick = useCallback(() => {
    if (!data?.links.previous) {
      return;
    }
    const url = new URLSearchParams(data.links.previous.split('?')[1]);
    const cursor = url.get('cursor');
    if (!cursor) {
      return;
    }
    onPrevPageClick(cursor);
  }, [data?.links.previous, onPrevPageClick]);

  const handleNextPageClick = useCallback(() => {
    if (!data?.links.next) {
      return;
    }
    const url = new URLSearchParams(data.links.next.split('?')[1]);
    const cursor = url.get('cursor');
    if (!cursor) {
      return;
    }
    onNextPageClick(cursor);
  }, [data?.links.next, onNextPageClick]);

  return (
    <>
      <DataTable dataSource={data?.items ?? []} columns={columns} getRowKey={record => record.id} loading={loading} />
      {!loading && (
        <Pagination>
          <div />
          <div>
            <PageButton disabled={!data?.links.previous} onClick={handlePreviousPageClick}>
              <CaretLeft />
            </PageButton>
            <PageButton disabled={!data?.links.next} onClick={handleNextPageClick}>
              <CaretRight />
            </PageButton>
          </div>
          <LegacyPopover
            renderArrow={false}
            showOnClick
            showOnHover={false}
            closeOnClickInside
            placement="bottom"
            content={
              <PageSizeOptions>
                {PAGE_SIZES.map(size => (
                  <PageSizeOption selected={size === pageSize} key={size} onClick={() => onPageSizeChange(size)}>
                    {size}
                  </PageSizeOption>
                ))}
              </PageSizeOptions>
            }
          >
            <TextButton iconPosition="end" icon={<CaretDown />}>
              {intl.formatMessage({ id: 'menu.restockingRules.showButton' }, { number: pageSize })}
            </TextButton>
          </LegacyPopover>
        </Pagination>
      )}
    </>
  );
};

export default RestockingRulesTable;
